import { blankFrame } from '../builder'
import { FRAME_LIST } from '../frames'
import { FrameId } from '../pipeline-types'
import { QueryResult, ViewConfig } from '../view-config'
import { ReportControls } from './ReportSetup'
import { ReportViewer } from './ReportViewer'
import { ReportView, resolveView } from './ViewSwitcher'

/**
 * Presentation-only view switch: changes only *how* the report is drawn
 * (`config.chart`), never its shape. It never drops `groupBy`/`seriesBy` or
 * collapses to a metric — that's the sidebar's job. `'table'` maps to the
 * chartless summary; `'metric'` is only offered for an ungrouped rollup (chart
 * irrelevant there), so it leaves `chart` untouched.
 */
export const setPresentationView = (
  c: ViewConfig,
  v: ReportView
): ViewConfig => ({
  ...c,
  chart: v === 'table' ? 'table-only' : v === 'metric' ? c.chart : v,
})

/**
 * The shared report editing surface: a "Show as" switcher above a live preview
 * (left) and the operations + filters sidebar (right).
 *
 * The switcher is purely presentational in both modes — it repaints among the
 * views the current shape supports (`availableViews`), changing `config.chart`
 * alone. It never reshapes: the report's shape (raw list vs summary, the
 * breakdown, the measure) is built entirely from the sidebar (`ReportControls`).
 * So picking "Bar" can never collapse a report to a metric — a metric is just a
 * summary with no breakdown, chosen on the right.
 *
 * `mode` only decides whether the frame picker shows: `'build'` lets a new
 * report switch its source frame; `'edit'` hides it (a saved report's frame is
 * fixed).
 *
 * The preview card is always full width (so the "Show as" row never wraps); only
 * the metric/number body is width-capped, since one big number shouldn't stretch
 * edge to edge.
 */
export function ReportEditorBody({
  config,
  result,
  onChange,
  mode = 'build',
}: {
  config: ViewConfig
  result: QueryResult
  onChange: (next: ViewConfig) => void
  mode?: 'build' | 'edit'
}) {
  // The view is a presentation choice clamped to what the current shape
  // supports, so the switcher only ever repaints (never reshapes).
  const view = resolveView(config, result)

  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
      {/* Main: live preview with "Show as" inside the card, above a divider. */}
      <div className="min-w-0 flex-1">
        <ReportViewer
          config={config}
          result={result}
          view={view}
          onViewChange={(v) => onChange(setPresentationView(config, v))}
        />
      </div>

      {/* Right sidebar: source (build only) + operations + filters. */}
      <aside className="w-full shrink-0 rounded-lg border border-line bg-surface p-4 lg:sticky lg:top-4 lg:w-80">
        {mode === 'build' && (
          // Frame picker: the report's starting table. Switching reseeds a blank
          // report for the new frame (its columns/measures differ), keeping the
          // name/description. Edit mode omits it — a saved report's frame is fixed.
          <div className="mb-3 flex flex-col border-b border-line pb-3">
            <span className="mb-1 text-xs text-fg-secondary">Source</span>
            <select
              aria-label="Report source"
              value={config.frame ?? 'deal'}
              onChange={(e) =>
                onChange({
                  ...blankFrame(e.target.value as FrameId),
                  name: config.name,
                  description: config.description,
                })
              }
              className="rounded-md border border-line-form bg-surface px-2 py-1.5 text-sm text-fg"
            >
              {FRAME_LIST.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
        )}
        <ReportControls config={config} onChange={onChange} />
      </aside>
    </div>
  )
}
